<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";

import { getSortedCompanyNeedPriorityOptions } from "@/modules/companyNeed/helpers/priority";

import type { LabelValue } from '@/types/common';

import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";

interface PriorityDropdownProps {
    modelValue: LabelValue | null;
    placeholder?: string;
    showIcon?: boolean;
    icon?: string;
    error?: boolean;
    width?: string;
    fullWidth?: boolean;
}

withDefaults(defineProps<PriorityDropdownProps>(), {
    width: "260px",
    showIcon: true,
    icon: "priority",
});

const emit = defineEmits<{
    "update:modelValue": [value: LabelValue | null];
}>();

const { t } = useI18n();

const open = ref(false);
const sortedOptions = getSortedCompanyNeedPriorityOptions();

const handleToggle = (isOpen: boolean) => {
    open.value = isOpen;
};

const handleSelect = (option: LabelValue) => {
    emit("update:modelValue", option);
    open.value = false;
};
</script>

<template>
    <Dropdown :open="open" :width="width" @toggle="handleToggle">
        <template #trigger-zone>
            <ComboButton :icon-left="showIcon ? icon : undefined" :error="error" :placeholder="!modelValue" :full-width="fullWidth">
                {{ modelValue?.label ?? placeholder ?? t("dropdowns.priority.placeholder") }}
            </ComboButton>
        </template>
        <template #dropdown-content>
            <DropdownItem
                v-for="option in sortedOptions"
                :key="option.value"
                :label="option.label"
                :icon-left="option.icon"
                :icon-left-color="option.color"
                :checked="modelValue?.value === option.value"
                @click="handleSelect(option)"
            />
        </template>
    </Dropdown>
</template>
